// Centralised German label helper.
//
// Forum boards / category slugs are stored as ASCII-safe strings (e.g.
// "waermepumpe", "foerderung") because they appear in URLs and queries.
// When we render them as headings, breadcrumbs, eyebrows, etc. we need to
// reverse the German transliteration so "waermepumpe" renders as "Wärmepumpe".
//
// Server-provided category maps (ForumPost::CATEGORIES, passed as the
// `categories` prop) already contain proper umlauts. Always prefer those
// when available — `categoryDisplay()` does that. The bare `germanLabel()`
// helper is for cases where only the slug is in scope.
//
// Important: this is presentation only. Never use these labels as IDs,
// URL fragments, or query keys — those must stay ASCII.

// Authoritative map of known forum / category slugs to their German display
// names. Mirrors `App\Models\ForumPost::CATEGORIES` plus a generous superset
// of PV / heat-pump / Förderung / Klima vocabulary that may show up as
// derived board names or future categories.
const KNOWN_LABELS = {
  // Forum categories (ForumPost::CATEGORIES)
  photovoltaik: 'Photovoltaik',
  waermepumpe: 'Wärmepumpe',
  speicher: 'Stromspeicher',
  foerderung: 'Förderung & Finanzen',
  installation: 'Installation & Technik',
  erfahrungen: 'Erfahrungsberichte',
  allgemein: 'Allgemein',

  // PV / heat-pump terms that may surface as board / category names
  waerme: 'Wärme',
  waermepumpen: 'Wärmepumpen',
  warmwasser: 'Warmwasser',
  heizung: 'Heizung',
  heizungen: 'Heizungen',
  klima: 'Klima',
  klimaanlage: 'Klimaanlage',
  solaranlage: 'Solaranlage',
  solaranlagen: 'Solaranlagen',
  solar: 'Solar',
  solarthermie: 'Solarthermie',
  pv: 'PV',
  stromspeicher: 'Stromspeicher',
  batteriespeicher: 'Batteriespeicher',
  wechselrichter: 'Wechselrichter',
  module: 'Module',
  wallbox: 'Wallbox',
  notstrom: 'Notstrom',
  netzanschluss: 'Netzanschluss',
  einspeisung: 'Einspeisung',
  eigenverbrauch: 'Eigenverbrauch',
  foerderprogramme: 'Förderprogramme',
  zuschuesse: 'Zuschüsse',
  finanzen: 'Finanzen',
  finanzierung: 'Finanzierung',
  steuer: 'Steuer',
  steuern: 'Steuern',
  haeufig: 'Häufig',
  groesse: 'Größe',
  groessen: 'Größen',
  hauseigentuemer: 'Hauseigentümer',
  vermieter: 'Vermieter',
  effizienz: 'Effizienz',
  oekologie: 'Ökologie',
  oekologisch: 'Ökologisch',
  umwelt: 'Umwelt',
  technik: 'Technik',
  service: 'Service',
  wartung: 'Wartung',
  reparatur: 'Reparatur',
  kabelschutzschienen: 'Kabelschutzschienen',
  kabel: 'Kabel',
  daemmung: 'Dämmung',
  sanierung: 'Sanierung',
  neubau: 'Neubau',
  altbau: 'Altbau',
  fassade: 'Fassade',
  dach: 'Dach',
  daecher: 'Dächer',
};

/**
 * Reverse the standard German ASCII transliteration where it's safe to do so,
 * for slugs we don't have an explicit entry for. We only flip the digraphs
 * that are unambiguously German (ae/oe/ue/ss) — and only at boundaries that
 * are unlikely to be false positives. This is a fallback; the explicit map
 * above is preferred.
 */
function untransliterate(slug) {
  if (!slug) return '';
  let s = String(slug);

  // Normalise separators and trim
  s = s.replace(/[_-]+/g, ' ').trim();
  if (!s) return '';

  // Lowercase for matching
  const lower = s.toLowerCase();

  // Restore ß for "ss" only when it appears between vowels or at end of a
  // syllable cluster — too risky to do generically, so we only do it for
  // common patterns: "...ss" at word end after a long vowel is intentionally
  // skipped (e.g. "Stress" stays). We *do* flip "gross" → "groß" via the
  // explicit map above. Skip generic ß restoration in fallback.

  // Restore umlauts: "ae"/"oe"/"ue" → "ä"/"ö"/"ü". Keep it simple and apply
  // at every occurrence — German rarely uses raw "ae"/"oe"/"ue" without
  // meaning the umlaut in our domain (heat-pump / solar terms).
  let restored = lower
    .replace(/ae/g, 'ä')
    .replace(/oe/g, 'ö')
    .replace(/ue/g, 'ü');

  // Title-case each whitespace-separated word.
  restored = restored
    .split(' ')
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

  return restored;
}

/**
 * Convert a slug-like string ("waermepumpe", "foerderung", "kabel-schutz")
 * to a presentable German label ("Wärmepumpe", "Förderung & Finanzen",
 * "Kabel Schutz"). Looks up the known map first, then falls back to a
 * conservative ASCII-back-to-umlaut transform.
 *
 * @param {string|null|undefined} slug
 * @param {{ fallback?: string }} [opts]
 * @returns {string}
 */
export function germanLabel(slug, opts = {}) {
  if (slug == null) return opts.fallback || '';
  const raw = String(slug).trim();
  if (!raw) return opts.fallback || '';

  // Normalise to lookup key: lowercase, strip surrounding punctuation,
  // collapse hyphens/underscores to nothing for known-map lookup.
  const lookupKey = raw.toLowerCase().replace(/[\s_-]+/g, '');
  if (KNOWN_LABELS[lookupKey]) return KNOWN_LABELS[lookupKey];

  // Also try the slug as-is (lowercase) in case it has hyphens preserved
  const lowerKey = raw.toLowerCase();
  if (KNOWN_LABELS[lowerKey]) return KNOWN_LABELS[lowerKey];

  // Fallback: ASCII-back-to-umlaut transform + title-case.
  return untransliterate(raw) || opts.fallback || raw;
}

/**
 * Uppercase variant for breadcrumbs / eyebrows where the design wants
 * "WÄRMEPUMPE" not "Wärmepumpe". Locale-aware uppercasing handles ä → Ä, ö → Ö, ü → Ü.
 *
 * @param {string|null|undefined} slug
 * @returns {string}
 */
export function germanLabelUpper(slug) {
  const label = germanLabel(slug);
  if (!label) return '';
  try { return label.toLocaleUpperCase('de-DE'); } catch (_) { return label.toUpperCase(); }
}

/**
 * Resolve a category slug to its display name, preferring the server-supplied
 * `categories` map (e.g. ForumPost::CATEGORIES, which already has proper
 * umlauts) and falling back to the centralised JS helper for cases where
 * the prop wasn't forwarded or the slug isn't in the map.
 *
 * @param {Record<string, string>|null|undefined} categoriesMap
 * @param {string|null|undefined} slug
 * @param {{ fallback?: string }} [opts]
 * @returns {string}
 */
export function categoryDisplay(categoriesMap, slug, opts = {}) {
  if (slug == null || slug === '') return opts.fallback || '';
  const key = String(slug);
  if (categoriesMap && typeof categoriesMap === 'object' && categoriesMap[key]) {
    return categoriesMap[key];
  }
  return germanLabel(key, opts);
}

/**
 * Uppercase form of `categoryDisplay()`, for breadcrumbs / eyebrows.
 *
 * @param {Record<string, string>|null|undefined} categoriesMap
 * @param {string|null|undefined} slug
 * @returns {string}
 */
export function categoryDisplayUpper(categoriesMap, slug) {
  const label = categoryDisplay(categoriesMap, slug);
  if (!label) return '';
  try { return label.toLocaleUpperCase('de-DE'); } catch (_) { return label.toUpperCase(); }
}

export default germanLabel;
